import { useEffect, useRef } from 'react';

import AudienceCard from '@klv/molecules/audience-card';
import FadeInUp from '@klv/utils/fade-in-up';
import useSlides from '@klv/hooks/useSlides';
import { generateClassName } from '@klv/shared/utils/functions';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import { SlideIndicators } from '@klv/molecules/slide-indicators';
import { translateCard } from '@klv/shared/utils/translateCard';
import Typography from '@klv/atoms/typography';
import useCarouselCardPosition from '@klv/hooks/useCarouselCardPosition';

import * as styles from './styles/audience.module.css';
import type { IAudience, IAudienceCards } from './types';

export const Audience = ({
  audienceCards,
  backgroundColor = 'dark',
  customPadding,
  headline,
}: IAudience) => {
  const initialRender = useRef(true);
  const { slides, setThisSlide, swipeable, slideState, swipe } = useSlides(audienceCards, ['left', 'right']);
  const { windowWidth, cardWidth, resizing } = useCarouselCardPosition('[data-audience-card]');

  useEffect( () => {
    if (initialRender.current === true) {
      initialRender.current = false;
    } else {
      const currentSlide = slideState.current + 1;
      gtmBasicEvent({ action: `card ${currentSlide}`, category: 'carousel scroll', label: 'audience', non_interaction_flag: false });
    } 
  }, [slideState, initialRender]);

  return (
  <div className={generateClassName([
    styles.audienceWrapper,
    styles[backgroundColor],
    `${customPadding.customPaddingBottom}PaddingBottom`,
    `${customPadding.customPaddingTop}PaddingTop`,
  ])}>
    <FadeInUp>
      <div className={generateClassName(['grid', styles.grid])}>
        <Typography
          className={styles.headline}
          as='h2'
          variant='h2'
          dangerouslySetInnerHTML={{ __html: headline }}
        />
      </div>
      <div className={styles.audienceContainer} {...swipeable}>
        <div className={generateClassName([
          'grid',
          styles.innerGrid,
        ])}>
            <div className={styles.audience}
              style={{
                transform: `translateX(${translateCard(windowWidth, cardWidth, 24, slideState.current)}px)`,
                transitionDuration: resizing ? '0s' : '0.5s',
              }}
            >
              {slides.map((row: IAudienceCards, rowIndex: number) => (
                <AudienceCard
                  className={styles.audienceCard}
                  backgroundColor={backgroundColor}
                  {...row.audienceCard}
                  $current={slideState.current === rowIndex} 
                  setThisSlide={setThisSlide} 
                  key={rowIndex} 
                  $index={rowIndex} 
                  $activeIndex={slideState.current} 
                  swipe={swipe}
                />
              ))}
          </div>
        </div>
      </div>
      <SlideIndicators
        slides={Array.from(Array(slides.length).keys())}
        backgroundColor={backgroundColor}
        currentSlide={slideState.current}
        setThisSlide={setThisSlide}
      />
    </FadeInUp>
  </div>
  );
};
